import { constant } from '@/constant';
import type { Community } from '@/store/communityStore';
import type { Page, PageBlock } from '@/store/page.interface';
import { getTranslation } from '@/utils/data-display-utils';

/** The block types that draw a page's `h1` themselves. A block that comes to draw one joins this list. */
export const HEADING_BLOCK_TYPES: readonly number[] = [constant.Block.Type.Hero, constant.Block.Type.HomepageHero];

/**
 * The heading a page layout draws as the page's `h1`: the page's title, or null when one of the blocks it draws
 * already draws the page's `h1`, so that a page has exactly one. Decided once, for every layout.
 */
export function pageHeading(page: Page, drawnBlocks: PageBlock[]): string | null {
  if (drawnBlocks.some((block) => HEADING_BLOCK_TYPES.includes(block.pageBlockType))) {
    return null;
  }
  return getTranslation(page, 'title', page.languageEnabled) || null;
}

/** The browser tab wherever no page names itself: the community's title. */
export function communityDocumentTitle(community: Community | null | undefined): string | null {
  return (community && getTranslation(community, 'title', community.languageEnabled)) || null;
}

/**
 * The browser tab of a page: « page title — community title ». The homepage keeps the community's title alone, which is
 * the name visitors search for; it is the page whose id the community names, wherever it is reached.
 */
export function pageDocumentTitle(
  page: Page,
  community: Community | null | undefined,
  homepageId: number | null | undefined,
): string | null {
  const communityTitle = communityDocumentTitle(community);
  const isHomepage = homepageId !== null && homepageId !== undefined && Number(page.id) === Number(homepageId);
  const pageTitle = getTranslation(page, 'title', page.languageEnabled);
  if (isHomepage || !pageTitle) {
    return communityTitle;
  }
  return communityTitle ? `${pageTitle} — ${communityTitle}` : pageTitle;
}
